import {useState, useEffect, useRef} from 'react'
import {Card} from '@/components/ui/card'
import {useQuery} from '@tanstack/react-query'
import {Sparkles, Loader2, Check} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Textarea} from '@/components/ui/textarea'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {CollapsibleCardHeader} from '@/components/shared/CollapsibleCardHeader'

interface SqlAssistPanelProps {
    onSuggestion: (sql: string) => void
    errorContext: string | null
    currentSql: string
    autoSubmit?: boolean
    mode?: string
}


function stripCodeFences(text: string): string {
    return text.replace(/^```(?:sql)?\s*\n?/i, '').replace(/\n?```\s*$/i, '').trim()
}

export function SqlAssistPanel({onSuggestion, errorContext, currentSql, autoSubmit, mode}: SqlAssistPanelProps) {
    const i18n = useI18n()
    const services = useServices()
    const [open, setOpen] = useState(false)
    const [request, setRequest] = useState('')
    const [suggestion, setSuggestion] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [selectedModel, setSelectedModel] = useState<string>('')

    const {data: models} = useQuery<string[]>({
        queryKey: ['sql-models'],
        queryFn: () => services.reporting.sqlModels(),
    })

    // Set default model when models load
    useEffect(() => {
        if (models?.length && !selectedModel) {
            setSelectedModel(models[0])
        }
    }, [models, selectedModel])

    // Auto-open and pre-fill when error context changes
    const autoSubmittedRef = useRef(false)
    useEffect(() => {
        if (errorContext) {
            setOpen(true)
            setRequest(`${i18n.reporting.assist.fixPrefix}: ${errorContext}`)
            setSuggestion(null)
            autoSubmittedRef.current = false
        }
    }, [errorContext, i18n.reporting.assist.fixPrefix])

    // Auto-submit when autoSubmit is true and we have a request + model ready
    useEffect(() => {
        if (autoSubmit && errorContext && selectedModel && !autoSubmittedRef.current && !isLoading) {
            autoSubmittedRef.current = true
            handleSuggest()
        }
    }, [autoSubmit, errorContext, selectedModel]) // eslint-disable-line react-hooks/exhaustive-deps

    async function handleSuggest() {
        if (!request.trim()) return
        setIsLoading(true)
        setSuggestion(null)
        try {
            const response = await services.reporting.sqlAssist({
                request: request.trim(),
                currentSql: currentSql || null,
                errorMessage: errorContext || null,
                model: selectedModel || null,
                mode: mode || null,  
            })
            setSuggestion(stripCodeFences(response.suggestion))
        } catch (err) {
            setSuggestion(null)
            throw err
        } finally {
            setIsLoading(false)
        }
    }

    function handleApply() {
        if (suggestion) {
            onSuggestion(suggestion)
            setSuggestion(null)
        }
    }

    return (
        <Card>
            <CollapsibleCardHeader
                open={open}
                onToggle={() => setOpen(!open)}
                data-testid="btn-toggle-assist"
            >
                <InlineRow gap="md" className="font-medium">
                    <Icon icon={Sparkles} size="sm" muted/>
                    {i18n.reporting.assist.title}
                </InlineRow>
            </CollapsibleCardHeader>

            {open && (
                <Stack gap="sm" className="px-3 pb-3">
                    <Textarea
                        data-testid="input-assist-request"
                        value={request}
                        onChange={(e) => setRequest(e.target.value)}
                        placeholder={i18n.reporting.assist.placeholder as string}
                        rows={2}
                        className="text-sm"
                    />
                    <InlineRow gap="md">
                        {models && models.length > 1 && (
                            <Select value={selectedModel} onValueChange={setSelectedModel}>
                                <SelectTrigger data-testid="select-model" className="w-48 h-8">
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    {models.map(m => (
                                        <SelectItem key={m} value={m}>{m}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        <Button
                            data-testid="btn-suggest-sql"
                            variant="outline"
                            size="sm"
                            onClick={handleSuggest}
                            disabled={isLoading || !request.trim()}
                        >
                            <Icon icon={isLoading ? Loader2 : Sparkles} size="sm" className={isLoading ? 'animate-spin' : undefined}/>
                            {isLoading ? i18n.reporting.assist.suggesting : i18n.reporting.assist.suggestSql}
                        </Button>
                    </InlineRow>

                    {suggestion && (
                        <Stack gap="sm">
                            <CodeBlock data-testid="suggested-sql-preview">
                                {suggestion}
                            </CodeBlock>
                            <Button
                                data-testid="btn-apply-suggestion"
                                variant="default"
                                size="sm"
                                onClick={handleApply}
                            >
                                <Icon icon={Check} size="sm"/>
                                {i18n.reporting.assist.apply}
                            </Button>
                        </Stack>
                    )}
                </Stack>
            )}
        </Card>
    )
}
